import React from "react";
import { Container, Box, Typography, Divider, Link as MuiLink } from "@mui/material";

const COLORS = {
  heading: "#0A0F2C",
  accent: "#1E3A8A",
  text: "#334155",
  subtle: "#64748B",
};

function Section({ id, title, children }) {
  return (
    <Box id={id} sx={{ mb: 5, scrollMarginTop: "100px" }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: COLORS.heading,
          mb: 1.5,
          pb: 1,
          borderBottom: "1px solid #E2E8F0",
        }}
      >
        {title}
      </Typography>
      <Typography
        component="div"
        sx={{ color: COLORS.text, fontSize: "0.95rem", lineHeight: 1.85 }}
      >
        {children}
      </Typography>
    </Box>
  );
}

const TOC = [
  ["scope", "1. Scope of This Privacy Policy"],
  ["info-we-collect", "2. Information We May Collect"],
  ["collection-methods", "3. How We Collect Information"],
  ["how-we-use", "4. How We Use Personal Information"],
  ["ai-processing", "5. AI and Automated Processing"],
  ["purpose-limitation", "6. Purpose Limitation"],
  ["data-roles", "7. Customer and Candidate Data Roles"],
  ["sharing", "8. How We Share Information"],
  ["third-party", "9. Third-Party Services and Integrations"],
  ["transfers", "10. International Data Transfers"],
  ["retention", "11. Data Retention"],
  ["security", "12. Data Security"],
  ["responsibilities", "13. User and Customer Responsibilities"],
  ["rights", "14. Candidate Privacy Rights"],
  ["cookies", "15. Cookies and Similar Technologies"],
  ["communications", "16. Communications"],
  ["children", "17. Children's Privacy"],
  ["breach", "18. Data Breach and Security Incidents"],
  ["business-transfers", "19. Business Transfers"],
  ["legal-disclosures", "20. Legal Disclosures"],
  ["deletion", "21. Data Deletion and Account Closure"],
  ["changes", "22. Changes to This Privacy Policy"],
  ["applicable-law", "23. Applicable Law"],
  ["grievance", "24. Grievance and Privacy Contact"],
  ["jurisdiction", "25. Jurisdiction"],
  ["acceptance", "26. Acceptance"],
];

function PrivacyPolicy() {
  return (
    <Box sx={{ background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 280px)" }}>
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Box
          sx={{
            width: 56,
            height: 5,
            borderRadius: 3,
            background: `linear-gradient(90deg, ${COLORS.accent}, #3B82F6)`,
            mb: 2.5,
          }}
        />
        <Typography
          sx={{
            color: COLORS.accent,
            fontWeight: 700,
            letterSpacing: 1,
            fontSize: "0.8rem",
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          Evolve X Innovations &middot; ExiHire
        </Typography>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            color: COLORS.heading,
            fontSize: { xs: "1.8rem", md: "2.6rem" },
            mb: 1,
          }}
        >
          Privacy Policy
        </Typography>
        <Typography sx={{ color: COLORS.subtle, mb: 3, fontSize: "0.9rem" }}>
          Effective Date: July 8, 2026 &nbsp;&middot;&nbsp; Last Updated: July 8, 2026
        </Typography>

        <Typography sx={{ color: COLORS.text, fontSize: "0.98rem", lineHeight: 1.85, mb: 5 }}>
          EVOLVE X INNOVATIONS (&ldquo;EVOLVE X INNOVATIONS&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;,
          or &ldquo;our&rdquo;) operates <strong>ExiHire</strong>, a cloud-based Software-as-a-Service
          (SaaS) recruitment, applicant tracking, candidate assessment, communication, and AI-enabled
          hiring platform. This Privacy Policy explains how we collect, use, process, store, disclose,
          and protect personal information when individuals, candidates, recruiters, employers,
          institutions, administrators, and other authorized users access or use ExiHire, its websites,
          applications, services, integrations, communications, and related features. By accessing or
          using ExiHire, you acknowledge the practices described in this Privacy Policy, subject to
          applicable law.
        </Typography>

        <Box
          sx={{
            display: { xs: "none", md: "grid" },
            gridTemplateColumns: "1fr 1fr",
            columnGap: 3,
            rowGap: 0.75,
            p: 3,
            mb: 6,
            border: "1px solid #E2E8F0",
            borderRadius: 3,
            bgcolor: "#F8FAFC",
          }}
        >
          {TOC.map(([id, label]) => (
            <MuiLink
              key={id}
              href={`#${id}`}
              underline="hover"
              sx={{ color: COLORS.text, fontSize: "0.88rem", py: 0.4, "&:hover": { color: COLORS.accent } }}
            >
              {label}
            </MuiLink>
          ))}
        </Box>

        <Section id="scope" title="1. Scope of This Privacy Policy">
          This Privacy Policy applies to personal information processed through ExiHire, including
          information relating to candidates and job applicants; recruiters and hiring teams; employers
          and organizational customers; interviewers and evaluators; platform administrators and
          authorized users; website visitors; individuals participating in AI-enabled interviews or
          assessments; and individuals receiving recruitment-related communications through the
          platform.
          <br />
          <br />
          Where an employer, recruiter, institution, or other customer uses ExiHire to collect and
          process candidate information, that customer may independently determine the purpose and
          manner of such processing. In such circumstances, the customer may have separate privacy
          obligations under applicable law.
        </Section>

        <Section id="info-we-collect" title="2. Information We May Collect">
          Depending on how ExiHire is used, we may collect or process the following categories of
          information.
          <br />
          <br />
          <strong>2.1 Account and Registration Information:</strong> full name, email address, mobile
          number, username, password or authentication credentials in protected form, user role,
          organization or company name, designation or job title, account preferences, and subscription
          and account status.
          <br />
          <br />
          <strong>2.2 Candidate and Job Application Information:</strong> resume or curriculum vitae,
          cover letters, employment history, educational qualifications, skills and technologies,
          certifications, professional experience, project information, portfolio details, job
          preferences, salary expectations where provided, notice period, availability, current and
          preferred location, application history, application status, recruiter notes, screening
          responses, and candidate-provided documents and supporting information.
          <br />
          <br />
          <strong>2.3 Recruitment and Interview Information:</strong> interview responses (written,
          audio, or video), interview recordings where enabled, interview transcripts, timestamps and
          duration, assessment and coding responses, technical, behavioral, and HR responses, evaluation
          notes, interview completion or termination status, recruiter feedback, and hiring workflow
          status.
          <br />
          <br />
          <strong>2.4 AI-Generated and Derived Information:</strong> candidate-role match indicators,
          shortlisting recommendations, candidate rankings, interview and follow-up questions, interview
          summaries, response analysis, scores, skill and technical assessment results, behavioral
          assessment insights, AI-generated reports, and other decision-support outputs.
          <br />
          <br />
          <strong>2.5 Communication Information:</strong> email addresses, mobile numbers, communication
          preferences, email delivery status, WhatsApp message status, voice calling activity, SMS
          activity where applicable, message metadata, communication timestamps, and delivery, failure,
          read, or response status where supported. The actual availability of these features may depend
          on the customer&rsquo;s subscription plan and third-party service providers.
          <br />
          <br />
          <strong>2.6 Billing and Transaction Information:</strong> customer or company name, billing
          address, GSTIN, invoice information, subscription plan, payment status, transaction reference,
          payment date, purchased credits, usage entitlements, custom quotation information, and
          enterprise plan information. Subscription payments are processed through Razorpay, our
          third-party payment gateway; payment card, banking, UPI, or other sensitive payment credentials
          are processed directly by Razorpay, and ExiHire does not store complete payment credentials.
          <br />
          <br />
          <strong>2.7 Technical and Usage Information:</strong> IP address, browser type, device type,
          operating system, login timestamps, session information, pages or features accessed, platform
          activity, error logs, security logs, diagnostic information, approximate location derived from
          technical information where legally permitted, and cookies and similar technologies.
        </Section>

        <Section id="collection-methods" title="3. How We Collect Information">
          We may collect information directly from users; from candidates submitting applications; from
          recruiters or employers; through uploaded resumes and documents; during interviews and
          assessments; through platform usage; through customer-authorized integrations; through
          communication services; from payment providers; through cookies and similar technologies;
          from third-party services where authorized and lawful; and through AI-enabled processing of
          information available within ExiHire workflows.
          <br />
          <br />
          Customers must ensure that information uploaded or submitted to ExiHire has been collected and
          shared lawfully.
        </Section>

        <Section id="how-we-use" title="4. How We Use Personal Information">
          We may use personal information to: create and manage accounts; authenticate users; provide
          ExiHire services; manage job postings; process job applications; support applicant tracking
          workflows; facilitate candidate screening; provide AI-assisted shortlisting; conduct or
          facilitate interviews; generate interview questions; analyze interview responses; generate
          scores, summaries, and reports; support recruitment decisions; facilitate candidate outreach;
          send transactional communications; process subscriptions and payments; generate invoices;
          provide customer support; improve platform performance; develop and improve features; prevent
          fraud and misuse; protect platform security; diagnose technical issues; maintain audit records;
          comply with legal obligations; and enforce contractual terms.
        </Section>

        <Section id="ai-processing" title="5. AI and Automated Processing">
          ExiHire uses artificial intelligence and automated technologies to support recruitment and
          hiring workflows. These systems may process information such as resumes, skills, experience,
          job requirements, interview responses, assessment responses, candidate application information,
          and recruitment workflow information to generate match indicators, candidate rankings,
          shortlisting assistance, interview questions and follow-up questions, scores, summaries,
          evaluation reports, and recruitment recommendations.
          <br />
          <br />
          AI outputs may contain errors, limitations, or inaccuracies. Customers and authorized hiring
          professionals remain responsible for reviewing outputs and making final recruitment and
          employment decisions. Where required by applicable law, appropriate human review should be
          incorporated before making decisions that materially affect individuals.
        </Section>

        <Section id="purpose-limitation" title="6. Purpose Limitation">
          We process personal information for legitimate and specified purposes connected with
          providing, securing, supporting, administering, and improving ExiHire and related services. We
          do not intentionally use personal information for materially unrelated purposes without
          appropriate authorization or another lawful basis where required.
        </Section>

        <Section id="data-roles" title="7. Customer and Candidate Data Roles">
          Depending on the circumstances: an employer, recruiter, institution, or organizational customer
          may determine why candidate information is collected and processed; EVOLVE X INNOVATIONS may
          process such information to provide ExiHire services on behalf of the customer; and EVOLVE X
          INNOVATIONS may independently determine certain processing activities necessary for account
          management, security, billing, legal compliance, fraud prevention, and platform administration.
          <br />
          <br />
          Customers are responsible for providing legally required notices and obtaining appropriate
          permissions, consents, or other lawful authorization for information they upload, collect, or
          process through ExiHire.
        </Section>

        <Section id="sharing" title="8. How We Share Information">
          We may share personal information only where reasonably necessary and permitted by applicable
          law, with: authorized customer organizations; recruiters and hiring teams; interviewers and
          evaluators; cloud hosting providers; AI technology providers; email service providers;
          messaging service providers (including WhatsApp); telecommunications and voice-calling
          providers; Razorpay and other payment service providers; analytics and monitoring providers;
          security and fraud prevention providers; professional advisers; government or regulatory
          authorities where legally required; and other service providers supporting ExiHire operations.
          Service providers are expected to process information for authorized purposes and subject to
          applicable contractual and legal obligations. We do not sell personal information.
        </Section>

        <Section id="third-party" title="9. Third-Party Services and Integrations">
          ExiHire may use or integrate with third-party services for functions such as cloud
          infrastructure, artificial intelligence services, email delivery, WhatsApp messaging, voice
          calling, SMS services, payment processing (Razorpay), analytics, monitoring, authentication,
          file storage, and other technical services. Third-party services may process information in
          accordance with their contractual responsibilities and applicable privacy terms. EVOLVE X
          INNOVATIONS is not responsible for independent processing performed by third parties outside
          our control.
        </Section>

        <Section id="transfers" title="10. International Data Transfers">
          Some service providers or technical infrastructure used by ExiHire may process or store
          information in jurisdictions outside the user&rsquo;s state or country. Where required by
          applicable law, reasonable measures shall be taken to support lawful cross-border processing
          and appropriate protection of personal information.
        </Section>

        <Section id="retention" title="11. Data Retention">
          We retain personal information only for as long as reasonably necessary for providing ExiHire
          services, maintaining active accounts, supporting recruitment workflows, fulfilling contractual
          obligations, maintaining business and transaction records, resolving disputes, preventing
          fraud, ensuring security, and complying with legal, tax, accounting, and regulatory obligations
          &mdash; including GST invoicing records under Indian law.
          <br />
          <br />
          Retention periods may vary depending on the type of information, customer instructions,
          subscription status, recruitment workflow status, and legal or contractual requirements.
          Following the applicable retention period, information may be deleted, anonymized, aggregated,
          or otherwise handled in accordance with applicable law and operational requirements.
        </Section>

        <Section id="security" title="12. Data Security">
          We use reasonable administrative, technical, and organizational safeguards designed to protect
          personal information against unauthorized access, disclosure, loss, misuse, alteration,
          destruction, and other security threats. Measures may include access controls, authentication
          mechanisms, encryption in transit, restricted administrative access, logging and monitoring,
          security reviews, backup procedures, and infrastructure protections. However, no
          internet-based service, software platform, transmission method, or storage system can
          guarantee absolute security.
        </Section>

        <Section id="responsibilities" title="13. User and Customer Responsibilities">
          Users and customers are responsible for maintaining secure passwords; protecting account
          credentials; restricting unauthorized account access; managing internal user permissions;
          ensuring lawful collection of candidate information; uploading only information they are
          authorized to process; and promptly reporting suspected unauthorized access or security
          incidents.
        </Section>

        <Section id="rights" title="14. Candidate Privacy Rights">
          Subject to applicable law, individuals may have rights relating to their personal information,
          including the ability to request access to personal information; request correction of
          inaccurate information; request deletion or erasure where legally applicable; withdraw consent
          where processing is based on consent; raise concerns regarding processing; request information
          about how personal information is handled; and exercise other rights available under
          applicable law.
          <br />
          <br />
          Certain requests may be subject to identity verification, legal retention requirements,
          contractual obligations, security requirements, and applicable exemptions. Where ExiHire
          processes candidate information on behalf of an employer or recruiter, candidates may need to
          direct certain requests to the relevant employer or recruiter that controls the recruitment
          process.
        </Section>

        <Section id="cookies" title="15. Cookies and Similar Technologies">
          ExiHire may use cookies and similar technologies to maintain sessions, authenticate users,
          remember preferences, improve functionality, understand platform usage, measure performance,
          detect fraud, and protect security. Users may manage certain cookie preferences through browser
          settings or available consent controls. Disabling essential cookies may affect platform
          functionality.
        </Section>

        <Section id="communications" title="16. Communications">
          We may send account notifications, security alerts, interview invitations, application
          updates, recruitment communications, payment confirmations, subscription notices, service
          announcements, support communications, and other transactional messages &mdash; including
          WhatsApp alerts and AI voice call notifications you have opted into. Marketing communications,
          where used, shall be handled in accordance with applicable law and available preference or
          opt-out mechanisms.
        </Section>

        <Section id="children" title="17. Children's Privacy">
          ExiHire is primarily intended for professional recruitment, employment, institutional, and
          business-related use. The platform is not intentionally designed to collect personal
          information from children below the age permitted under applicable law without appropriate
          authorization. If we become aware that personal information has been collected contrary to
          applicable legal requirements, reasonable steps may be taken to address the matter.
        </Section>

        <Section id="breach" title="18. Data Breach and Security Incidents">
          In the event of a personal data breach or security incident, EVOLVE X INNOVATIONS may
          investigate the incident, take reasonable containment and remediation measures, notify
          affected customers, notify individuals where legally required, and notify relevant authorities
          where legally required. Notifications shall be handled in accordance with applicable law and
          the circumstances of the incident.
        </Section>

        <Section id="business-transfers" title="19. Business Transfers">
          If EVOLVE X INNOVATIONS undergoes a merger, acquisition, restructuring, financing transaction,
          sale of assets, or similar business transaction, personal information may be transferred as
          part of that transaction, subject to applicable legal requirements and appropriate safeguards.
        </Section>

        <Section id="legal-disclosures" title="20. Legal Disclosures">
          We may disclose personal information where reasonably necessary to comply with applicable law;
          respond to lawful government requests; comply with court orders; protect legal rights;
          investigate fraud; prevent security threats; enforce agreements; and protect users, candidates,
          customers, EVOLVE X INNOVATIONS, or third parties.
        </Section>

        <Section id="deletion" title="21. Data Deletion and Account Closure">
          Customers may request account closure subject to outstanding payment obligations, contractual
          requirements, legal retention obligations, security requirements, and active enterprise
          agreements. Deletion of an account may not result in immediate deletion of all information
          where retention is required for legal, regulatory, tax, security, dispute resolution, backup,
          or contractual purposes.
        </Section>

        <Section id="changes" title="22. Changes to This Privacy Policy">
          EVOLVE X INNOVATIONS may update this Privacy Policy periodically to reflect changes in ExiHire
          features, technology, business practices, legal requirements, or third-party integrations. The
          updated policy may be published with a revised &ldquo;Last Updated&rdquo; date. Where required
          by applicable law, additional notice may be provided for material changes.
        </Section>

        <Section id="applicable-law" title="23. Applicable Law">
          This Privacy Policy shall be interpreted in accordance with applicable laws of India, including
          applicable data protection, information technology, and privacy requirements, as amended from
          time to time.
        </Section>

        <Section id="grievance" title="24. Grievance and Privacy Contact">
          For privacy-related questions, complaints, requests, or grievances concerning ExiHire, users
          may contact our Privacy Team at{" "}
          <MuiLink href="mailto:connect@exihire.com" sx={{ color: COLORS.accent, fontWeight: 600 }}>
            connect@exihire.com
          </MuiLink>
          . Requests may be subject to reasonable identity verification before processing.
        </Section>

        <Section id="jurisdiction" title="25. Jurisdiction">
          Subject to applicable law, disputes arising in connection with this Privacy Policy shall be
          subject to the jurisdiction of the competent courts at Vijayawada, Andhra Pradesh, India.
        </Section>

        <Section id="acceptance" title="26. Acceptance">
          By accessing or using ExiHire, users acknowledge that they have been informed of the privacy
          practices described in this Privacy Policy, subject to any additional notices, permissions,
          consents, customer agreements, or legal requirements that may apply.
        </Section>

        <Divider sx={{ my: 4 }} />
        <Typography sx={{ color: COLORS.subtle, fontSize: "0.85rem", textAlign: "center" }}>
          EVOLVE X INNOVATIONS &middot; ExiHire &mdash; Vijayawada, Andhra Pradesh, India
        </Typography>
      </Container>
    </Box>
  );
}

export default PrivacyPolicy;
